import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { CreditCard } from 'lucide-react';
import ModalShell, { ModalFooter } from '../common/ModalShell';
import { useTranslation } from 'react-i18next';
import {
  getSubscriptionPackages, getDoctorPackage, assignDoctorPackage,
} from '../../redux/subscriptionSlice';
import { BILLING_PERIODS } from '../../data/accessControls';
import SearchableSelect from '../common/SearchableSelect';
import { subscriptionPrice } from '../../utils/currency';

// A period price in its package's currency (utils/currency.js, P24).
const money = (p, pkg) => (p == null || String(p) === '' ? '' : subscriptionPrice(p, pkg));

// Admin-only: assign / change a doctor's subscription package (per-row on the
// Doctors list). Free is a no-charge comp but one-time (blocked once used). A paid
// plan needs a billing period and becomes a PENDING change — the doctor is asked
// to pay (crediting their current plan's unused value) on their next login.
const AssignPackageModal = ({ doctor, onClose }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { packages, assigning } = useSelector((s) => s.subscription);
  const [selected, setSelected] = useState(''); // packageId, or '' = default (Free)
  const [period, setPeriod] = useState('');
  const [freeUsed, setFreeUsed] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      if (!packages.length) dispatch(getSubscriptionPackages());
      const res = await dispatch(getDoctorPackage(doctor.doctorId));
      if (active) {
        setSelected(res.payload?.packageId || '');
        setFreeUsed(!!res.payload?.freeUsed);
        setLoading(false);
      }
    })();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doctor.doctorId]);

  const defaultPkg = packages.find((p) => p.isDefault);
  const selectedPkg = packages.find((p) => p.id === selected) || null;
  const isPaid = !!selectedPkg && !selectedPkg.isDefault;

  // Enabled + priced periods for the selected paid plan.
  const bp = selectedPkg?.billingPeriods || {};
  const periods = BILLING_PERIODS
    .filter((x) => bp[x.id] && bp[x.id].enabled && String(bp[x.id].price ?? '').trim() !== '')
    .map((x) => ({ ...x, price: bp[x.id].price }));
  const needsPeriod = isPaid && periods.length > 0;

  // Default the period to the first option when the selected plan changes.
  useEffect(() => {
    if (needsPeriod) {
      setPeriod((prev) => (periods.some((p) => p.id === prev) ? prev : periods[0].id));
    } else {
      setPeriod('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  const canSave = !loading && !assigning && !(needsPeriod && !period);

  const save = async () => {
    try {
      const res = await dispatch(assignDoctorPackage({
        doctorId: doctor.doctorId,
        packageId: selected || null,
        period: needsPeriod ? period : undefined,
      })).unwrap();
      toast.success(res?.pending
        ? 'Plan set — the doctor will be asked to pay on their next login.'
        : 'Subscription package updated');
      onClose();
    } catch (e) {
      toast.warn(typeof e === 'string' ? e : 'Could not update package — is the backend deployed?');
    }
  };

  return (
    <ModalShell
      title="Subscription package"
      onClose={onClose}
      size="md"
      testId="assign-package-modal"
      footer={(
        <ModalFooter
          onCancel={onClose}
          onSubmit={save}
          submitLabel={assigning ? 'Saving…' : 'Save'}
          disabled={!canSave}
          submitTestId="assign-package-save"
        />
      )}
    >
        <div className="space-y-4">
          <p className="text-sm text-slate-500">
            Assign a package to <span className="font-semibold text-gray-700">{doctor.name || t('common.doctor_label', 'Doctor')}</span>.
          </p>

          {loading ? (
            <div className="py-6 flex justify-center">
              <div className="h-6 w-6 border-2 border-[#4ABDBD] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">Package</label>
                {/* The default row stays VISIBLE but unselectable once the free
                    plan is used. Hiding it would leave "where did Free go?"
                    unanswered on screen; showing it selectable would let
                    somebody choose what the server then refuses. */}
                <SearchableSelect
                  testId="assign-package-select"
                variant="form"
                  value={selected}
                  onChange={setSelected}
                  options={packages
                    .filter((p) => !p.isDefault && p.purchasable !== false)
                    .map((p) => ({ value: p.id, label: p.name }))}
                  placeholder={`Default (${defaultPkg ? defaultPkg.name : 'Free'})`}
                  placeholderDisabled={freeUsed}
                  placeholderNote={freeUsed ? '— already used' : ''}
                  searchPlaceholder="Search plans…"
                  className="w-full"
                />
                {freeUsed && (
                  <p className="text-[11px] text-amber-600 mt-1.5">This doctor has already used their free plan — choose a paid plan.</p>
                )}
              </div>

              {/* Billing period — required for a paid plan with periods */}
              {needsPeriod && (
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1.5">Billing period</label>
                  <div className="grid grid-cols-2 gap-2">
                    {periods.map((p) => {
                      const on = p.id === period;
                      return (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => setPeriod(p.id)}
                          data-testid={`assign-period-${p.id}`}
                          className={`text-left rounded-xl border px-3 py-2 transition-all ${on ? 'border-[#4ABDBD] ring-1 ring-[#4ABDBD] bg-[#4ABDBD]/5' : 'border-gray-200 hover:border-gray-300'}`}
                        >
                          <div className="text-sm font-semibold text-gray-700">{p.label}</div>
                          <div className="text-xs text-slate-500">{money(p.price, selectedPkg)} · {p.days} days</div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {isPaid ? (
                <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
                  <CreditCard className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>The doctor will be <strong>asked to pay</strong> for this plan on their next login. Any unused value from their current plan is credited.</span>
                </div>
              ) : (
                <p className="text-xs text-slate-400">
                  “Default” puts the doctor on the free plan (no charge). Selecting a paid plan asks the doctor to pay.
                </p>
              )}
            </>
          )}
        </div>

    </ModalShell>
  );
};

export default AssignPackageModal;
